import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { User, MapPin, Phone, Building, Calendar, FileText, UserCheck, Navigation, Map, X, Search, Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { createCustomer, selectCreatingLoading } from '../store/slices/customersSlice';
import { selectEmployee } from '../store/slices/authSlice';
import { CreateCustomerData } from '../lib/types';
import { useBackgroundGPS } from '../hooks/useBackgroundGPS';
import { useFormSubmitToast } from '../hooks/useSubmitToast';
import { locationService } from '../utils/locationService';
import { LocationPicker } from './ui/LocationPicker';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from './ui/badge';
import { directus } from '../lib/directus';
import { readItems } from '@directus/sdk';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';

interface NewCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface DataCustomerRecord {
  id?: number; // Optional - not always available due to permissions
  No_pelanggan: string;
  Nama_Pelanggan: string;
  Alamat?: string;
  Kontak?: string;
  Email?: string;
  Kota?: string;
  Provinsi?: string;
  latitude?: number; // Optional - not always available due to permissions
  longitude?: number; // Optional - not always available due to permissions
  source_table: 'Data_Customer' | 'Customer_Tambak';
}

interface PhoneCustomerRecord {
  id?: number; // Optional - not always available due to permissions
  No_pelanggan?: string;
  Phone_Number?: string;
  Phone_Parsed?: string;
}

interface DuplicateResult {
  type: 'name' | 'phone' | 'both';
  nameMatches?: DataCustomerRecord[];
  phoneMatches?: PhoneCustomerRecord[];
  message: string;
}

interface ServerResponse {
  success: boolean;
  message: string;
  customer?: any;
}

export const NewCustomerModal: FC<NewCustomerModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  const creatingLoading = useAppSelector(selectCreatingLoading);
  const currentEmployee = useAppSelector(selectEmployee);
  
  // Get current user's full name with fallbacks
  const currentUserName = currentEmployee?.full_name ||
                         `${currentEmployee?.first_name || ''} ${currentEmployee?.last_name || ''}`.trim() ||
                         'Unknown User';

  // Toast hook for immediate feedback
  const { createItem } = useFormSubmitToast();

  const [formData, setFormData] = useState<CreateCustomerData>({
    NamaPelangganTambak: '',
    Alamat: '',
    ukuranTambak: '',
    Contacs: '',
    tahunDibangun: undefined,
    approval_notes: '',
    statusAktif: 1,
    farmIsActive: true, // Default to active farm
    coordinates: undefined // GPS coordinates
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Initialize background GPS - starts automatically when modal opens
  const {
    coordinates,
    lastUpdated,
    error: gpsError,
    accuracy
  } = useBackgroundGPS({
    autoStart: false, // We'll start manually when modal opens
    updateInterval: 10000, // Update every 10 seconds
    enableHighAccuracy: true,
    timeout: 15000 // 15 seconds timeout
  });

  // State for GPS timeout handling
  const [gpsTimeoutId, setGpsTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [isGpsTimedOut, setIsGpsTimedOut] = useState(false);
  
  // State for location picker
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [manualCoordinates, setManualCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [locationMode, setLocationMode] = useState<'auto' | 'manual'>('auto');

  // Improved location tracking with fallbacks
  const [locationResult, setLocationResult] = useState<{
    coordinates: { lat: number; lng: number } | null;
    source: 'gps' | 'telegram' | 'ip' | 'default' | null;
    error: string | null;
    isCenteringOnly: boolean;
  }>({
    coordinates: null,
    source: null,
    error: null,
    isCenteringOnly: false
  });

  // Data_Customer search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DataCustomerRecord[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  // Duplicate checking states
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateResult | null>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Server response states
  const [showServerResponse, setShowServerResponse] = useState(false);
  const [serverResponse, setServerResponse] = useState<ServerResponse | null>(null);
  const pendingSubmissionDataRef = useRef<any>(null);

  // Location service instance (use singleton)
  // const locationService = useRef(new LocationService()).current;

  // Get location with fallback hierarchy (GPS → IP → Default)
  const getLocationWithFallbacks = useCallback(async (): Promise<{
    coordinates: { lat: number; lng: number } | null;
    source: 'gps' | 'telegram' | 'ip' | 'default' | null;
    error: string | null;
    isCenteringOnly: boolean;
  }> => {
    try {
      console.log('🔍 Starting location acquisition with fallback hierarchy...');

      const result = await locationService.getCurrentLocation({
        enableHighAccuracy: true,
        timeout: 15000, // 15 seconds
        fallbackToIP: true,
        telegramWebApp: window.Telegram?.WebApp || null
      });

      if (result.location && result.source) {
        const isLessAccurate = result.source === 'ip' || result.source === 'default';
        const coordinates = {
          lat: result.location.latitude,
          lng: result.location.longitude
        };

        console.log(`✅ Location acquired via ${result.source}:`, coordinates);

        return {
          coordinates,
          source: result.source,
          error: null,
          isCenteringOnly: isLessAccurate // IP and default locations are for map centering only
        };
      } else {
        throw new Error(result.error || 'Unable to acquire location');
      }
    } catch (error) {
      console.error('❌ Location acquisition failed:', error);
      return {
        coordinates: null,
        source: null,
        error: error instanceof Error ? error.message : 'Location acquisition failed',
        isCenteringOnly: false
      };
    }
  }, [locationService]);

  // Retry location acquisition with user feedback
  const retryLocationAcquisition = useCallback(async () => {
    // Clear previous error
    setLocationResult(prev => ({ ...prev, error: null }));

    try {
      const { showInfo, showError } = useFormSubmitToast();

      showInfo(t('new_customer.location.acquiring'), {
        duration: 1000
      });

      const result = await getLocationWithFallbacks();
      setLocationResult(result);

      if (result.coordinates) {
        // Update form data if we have coordinates and we're in auto mode
        if (locationMode === 'auto') {
          setFormData(prev => ({
            ...prev,
            coordinates: result.coordinates
          }));
        }

        // Show appropriate message based on location source
        if (result.source === 'gps') {
          showInfo(t('new_customer.location.gps_success'), { duration: 2000 });
        } else if (result.source === 'ip') {
          showInfo(t('new_customer.location.ip_location'), { duration: 3000 });
        } else if (result.source === 'default') {
          showInfo(t('new_customer.location.default_location'), { duration: 4000 });
        }
      } else {
        showError(t('new_customer.location.acquire_failed'));
      }
    } catch {
      const { showError } = useFormSubmitToast();
      showError(t('new_customer.location.acquisition_failed'));
    }
  }, [getLocationWithFallbacks, locationMode, useFormSubmitToast]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.NamaPelangganTambak.trim()) {
      newErrors.NamaPelangganTambak = t('new_customer.validation.name_required');
    }

    if (!formData.Alamat?.trim()) {
      newErrors.Alamat = t('new_customer.validation.address_required');
    }

    if (!formData.ukuranTambak?.trim()) {
      newErrors.ukuranTambak = t('new_customer.validation.size_required');
    }

    if (formData.tahunDibangun && (formData.tahunDibangun < 1900 || formData.tahunDibangun > new Date().getFullYear())) {
      newErrors.tahunDibangun = t('new_customer.validation.year_invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Check for duplicates in Data_Customer, Customer_Tambak (by name) and Phone_Customer (by phone)
   * Uses Directus _icontains filter (SQL LIKE equivalent)
   */
  const checkForDuplicates = useCallback(async (
    customerName: string,
    phoneNumbers: string[]
  ): Promise<DuplicateResult | null> => {
    const nameMatches: DataCustomerRecord[] = [];
    const phoneMatches: PhoneCustomerRecord[] = [];

    // 🔍 DEBUG: Log search parameters
    console.log('🔍 DUPLICATE CHECK START');
    console.log('📝 Customer Name:', customerName);
    console.log('📞 Phone Numbers:', phoneNumbers);
    console.log('📝 Name trimmed:', customerName?.trim());

    try {
      // Search in BOTH Data_Customer AND Customer_Tambak for name duplicates
      if (customerName.trim()) {
        console.log('✅ Name is not empty, proceeding with name search:', customerName.trim());
      } else {
        console.log('⚠️ Name is empty, skipping name search');
      }

      if (customerName.trim()) {
        // First, search Data_Customer
        try {
          console.log('🔍 Searching Data_Customer...');
          const dataCustomerResults = await directus.request(
            readItems('Data_Customer' as any, {
              filter: {
                Nama_Pelanggan: { _icontains: customerName.trim() }
              },
              fields: ['No_pelanggan', 'Nama_Pelanggan', 'Alamat'], // Using minimal fields to avoid permission issues
              limit: 5
            })
          ) as any[];

          console.log('✅ Data_Customer results:', dataCustomerResults.length, dataCustomerResults);

          // Add source table marker to Data_Customer results
          const mappedDataCustomers = dataCustomerResults.map(customer => ({
            ...customer,
            source_table: 'Data_Customer' as const
          }));

          nameMatches.push(...mappedDataCustomers);
          console.log('📊 Total nameMatches after Data_Customer:', nameMatches.length);
        } catch (dataError: any) {
          console.error('❌ Data_Customer search failed:', dataError);
          console.error('Error status:', dataError?.status);
          console.error('Error message:', dataError?.message);
          console.error('Errors array:', dataError?.errors);
          // Log full error for debugging
          console.error('Full error object:', JSON.stringify(dataError, null, 2));
        }

        // Second, search Customer_Tambak
        try {
          console.log('🔍 Searching Customer_Tambak...');
          const customerTambakResults = await directus.request(
            readItems('Customer_Tambak' as any, {
              filter: {
                Nama_Pelanggan_Tambak: { _icontains: customerName.trim() }
              },
              fields: ['No_pelanggan', 'Nama_Pelanggan_Tambak', 'Alamat', 'Contacs'],
              limit: 5
            })
          ) as any[];

          console.log('✅ Customer_Tambak results:', customerTambakResults.length, customerTambakResults);

          // Map Customer_Tambak results to same structure
          const mappedCustomerTambak = customerTambakResults.map(customer => ({
            No_pelanggan: customer.No_pelanggan || '',
            Nama_Pelanggan: customer.Nama_Pelanggan_Tambak || '',
            Alamat: customer.Alamat,
            Kontak: customer.Contacs,
            source_table: 'Customer_Tambak' as const
          }));

          nameMatches.push(...mappedCustomerTambak);
          console.log('📊 Total nameMatches after Customer_Tambak:', nameMatches.length);
        } catch (tambakError: any) {
          console.error('❌ Customer_Tambak search failed:', tambakError);
          console.error('Error status:', tambakError?.status);
          console.error('Error message:', tambakError?.message);
          console.error('Errors array:', tambakError?.errors);
          // Log full error for debugging
          console.error('Full error object:', JSON.stringify(tambakError, null, 2));
        }
      }

      // Check Phone_Customer for phone number duplicates using EXACT MATCH
      if (phoneNumbers.length > 0) {
        try {
          // Build exact match filter for each phone number
          // Search both Phone_Parsed and Phone_Number fields for exact matches
          const exactMatchFilters = phoneNumbers.flatMap(phone => [
            { Phone_Parsed: { _eq: phone } },
            { Phone_Number: { _eq: phone } }
          ]);

          const phoneResults = await directus.request(
            readItems('Phone_Customer' as any, {
              filter: {
                _or: exactMatchFilters
              },
              fields: ['No_pelanggan', 'Phone_Number', 'Phone_Parsed'],
              limit: 10
            })
          ) as PhoneCustomerRecord[];

          phoneMatches.push(...phoneResults);
        } catch (phoneError: any) {
          console.warn('⚠️ Phone_Customer search failed:', phoneError);
          console.warn('Error status:', phoneError?.status);
          console.warn('Error message:', phoneError?.message);
        }
      }

      // If no duplicates found, return null
      if (nameMatches.length === 0 && phoneMatches.length === 0) {
        console.log('✅ NO DUPLICATES: Name matches:', nameMatches.length, 'Phone matches:', phoneMatches.length);
        return null;
      }

      // Determine duplicate type and create message
      let type: 'name' | 'phone' | 'both' = 'both';
      if (nameMatches.length > 0 && phoneMatches.length === 0) {
        type = 'name';
      } else if (nameMatches.length === 0 && phoneMatches.length > 0) {
        type = 'phone';
      }

      // Build detailed message
      const messages: string[] = [];
      if (nameMatches.length > 0) {
        const dataCustomerCount = nameMatches.filter(m => m.source_table === 'Data_Customer').length;
        const customerTambakCount = nameMatches.filter(m => m.source_table === 'Customer_Tambak').length;

        if (dataCustomerCount > 0 && customerTambakCount > 0) {
          messages.push(`Name "${customerName}" found in Data_Customer (${dataCustomerCount}) and Customer_Tambak (${customerTambakCount}).`);
        } else if (dataCustomerCount > 0) {
          messages.push(`Name "${customerName}" found in Data_Customer (${dataCustomerCount} match${dataCustomerCount > 1 ? 'es' : ''}).`);
        } else if (customerTambakCount > 0) {
          messages.push(`Name "${customerName}" found in Customer_Tambak (${customerTambakCount} match${customerTambakCount > 1 ? 'es' : ''}).`);
        }
      }

      if (phoneMatches.length > 0) {
        messages.push(`Phone number(s) found in Phone_Customer (${phoneMatches.length} match${phoneMatches.length > 1 ? 'es' : ''}).`);
      }

      return {
        type,
        nameMatches: nameMatches.length > 0 ? nameMatches : undefined,
        phoneMatches: phoneMatches.length > 0 ? phoneMatches : undefined,
        message: messages.join('\n')
      };
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      // On error, allow to proceed (fail-safe)
      console.log('⚠️ ERROR in duplicate check, returning null');
      return null;
    }
  }, []);

  /**
   * Proceed with customer creation after duplicate check
   */
  const proceedWithCreation = useCallback(async () => {
    const submissionData = pendingSubmissionDataRef.current;
    if (!submissionData) return;

    setIsCheckingDuplicates(true);

    try {
      await createItem(
        () => dispatch(createCustomer(submissionData)).unwrap(),
        {
          entityName: t('customer'),
          loading: 'Creating customer farm...',
          success: 'Customer farm created successfully!',
          error: 'Failed to create customer farm'
        }
      );

      // Show success dialog
      setServerResponse({
        success: true,
        message: 'Customer farm created successfully!'
      });
      setShowServerResponse(true);

      // Reset form
      setTimeout(() => {
        setFormData({
          NamaPelangganTambak: '',
          Alamat: '',
          ukuranTambak: '',
          Contacs: '',
          tahunDibangun: undefined,
          approval_notes: '',
          statusAktif: 1,
          farmIsActive: true,
          coordinates: undefined
        });
        setErrors({});
        setManualCoordinates(null);
        setLocationMode('auto');
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
        setDuplicateResult(null);
        setShowDuplicateWarning(false);
        pendingSubmissionDataRef.current = null;
        setIsCheckingDuplicates(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to create customer:', error);

      // Show error dialog
      setServerResponse({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create customer farm'
      });
      setShowServerResponse(true);
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, [createItem, dispatch, t]);

  /**
   * Handle duplicate warning dialog response
   */
  const handleDuplicateWarningResponse = useCallback((keepAdding: boolean) => {
    setShowDuplicateWarning(false);

    if (keepAdding) {
      // User wants to proceed despite duplicates
      proceedWithCreation();
    } else {
      // User cancelled - return to form
      setIsCheckingDuplicates(false);
    }
  }, [proceedWithCreation]);

  /**
   * Handle server response dialog close
   */
  const handleServerResponseClose = useCallback(() => {
    setShowServerResponse(false);
    setServerResponse(null);

    if (serverResponse?.success) {
      onSuccess?.();
      onClose();
    }
  }, [onSuccess, onClose, serverResponse]);

  /**
   * Search Data_Customer table using Directus with _icontains filter (SQL LIKE)
   * Searches across Nama_Pelanggan, No_pelanggan, and Alamat fields
   */
  const searchDataCustomer = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await directus.request(readItems('Data_Customer' as any, {
        filter: {
          _or: [
            { Nama_Pelanggan: { _icontains: query } },
            { No_pelanggan: { _icontains: query } },
            { Alamat: { _icontains: query } }
          ]
        },
        fields: ['id', 'No_pelanggan', 'Nama_Pelanggan', 'Alamat', 'Kontak', 'Email', 'Kota', 'Provinsi'],
        limit: 10,
        sort: ['Nama_Pelanggan']
      }));

      setSearchResults(results as DataCustomerRecord[]);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching Data_Customer:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Debounced search to avoid excessive API calls
   */
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery && formData.NamaPelangganTambak !== searchQuery) {
        searchDataCustomer(searchQuery);
      } else if (!searchQuery) {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchDataCustomer, formData.NamaPelangganTambak]);

  /**
   * Handle selection from search results
   */
  const handleSelectSearchResult = useCallback((customer: DataCustomerRecord) => {
    setFormData(prev => ({
      ...prev,
      NamaPelangganTambak: customer.Nama_Pelanggan || '',
      Alamat: customer.Alamat || '',
      Contacs: customer.Kontak || ''
    }));
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);

    // Clear the name field error when a customer is selected
    if (errors.NamaPelangganTambak) {
      setErrors(prev => ({ ...prev, NamaPelangganTambak: '' }));
    }
  }, [errors]);

  /**
   * Handle name input change with search
   */
  const handleNameInputChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, NamaPelangganTambak: value }));
    setSearchQuery(value);

    // Clear error when user starts typing
    if (errors.NamaPelangganTambak) {
      setErrors(prev => ({ ...prev, NamaPelangganTambak: '' }));
    }
  }, [errors]);

  /**
   * Close search results when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Form submit started');

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }
    console.log('✅ Form validation passed');

    // Require coordinates from either improved location service or manual selection
    const finalCoordinates = locationMode === 'manual' ? manualCoordinates : locationResult.coordinates;
    if (!finalCoordinates) {
      console.warn('❌ Cannot submit customer without location coordinates');
      return;
    }
    console.log('✅ Coordinates OK:', finalCoordinates);

    // Extract and validate phone numbers from Contacs field using exact match search
    const phoneNumbers = extractPhoneNumbers(formData.Contacs || '');
    console.log('📞 Extracted phone numbers:', phoneNumbers);

    // Update form data with final coordinates and remove Contacs (phone numbers handled separately)
    const submissionData = {
      ...formData,
      coordinates: finalCoordinates,
      phoneNumbers: phoneNumbers // Pass cleaned phone numbers separately
    };

    // Store submission data for later use
    pendingSubmissionDataRef.current = submissionData;

    // Check for duplicates with loading state
    setIsCheckingDuplicates(true);
    console.log('🔍 About to call checkForDuplicates...');

    try {
      console.log('🔍 Calling checkForDuplicates with:', {
        name: formData.NamaPelangganTambak,
        phones: phoneNumbers
      });

      const duplicates = await checkForDuplicates(formData.NamaPelangganTambak, phoneNumbers);

      setIsCheckingDuplicates(false);

      console.log('🔍 DUPLICATES RESULT:', duplicates);
      console.log('🔊 nameMatches:', duplicates?.nameMatches?.length, duplicates?.nameMatches);
      console.log('🔊 phoneMatches:', duplicates?.phoneMatches?.length, duplicates?.phoneMatches);

      if (duplicates) {
        // Duplicates found - show warning dialog
        console.log('⚠️ DUPLICATES FOUND - Showing warning dialog');
        setDuplicateResult(duplicates);
        setShowDuplicateWarning(true);
      } else {
        // No duplicates - proceed directly to creation
        console.log('✅ NO DUPLICATES - Proceeding with creation');
        proceedWithCreation();
      }
    } catch (error) {
      setIsCheckingDuplicates(false);
      console.error('Error during duplicate check:', error);
      // On error, proceed anyway (fail-safe)
      console.log('⚠️ ERROR - Proceeding anyway due to error');
      proceedWithCreation();
    }
  };

  const handleInputChange = (field: keyof CreateCustomerData, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Clean and validate phone number input
   * - Allows + prefix
   * - Removes dashes, spaces, and other non-digit characters (except leading +)
   * - Validates 5-20 digit length
   */
  const cleanPhoneNumber = useCallback((phone: string): string => {
    if (!phone) return '';

    // Remove all whitespace first
    let cleaned = phone.replace(/\s+/g, '');

    // Check if it starts with +
    const hasPlus = cleaned.startsWith('+');

    // Remove everything except digits
    cleaned = cleaned.replace(/[^\d]/g, '');

    // Add back + if it was there
    if (hasPlus) {
      cleaned = '+' + cleaned;
    }

    // Validate length (5-20 digits, excluding +)
    const digitOnly = cleaned.replace(/^\+/, '');
    if (digitOnly.length < 5 || digitOnly.length > 20) {
      return phone; // Return original if invalid
    }

    return cleaned;
  }, []);

  /**
   * Extract valid phone numbers from contacts field
   * Handles comma-separated values with auto-cleanup
   */
  const extractPhoneNumbers = useCallback((contactsField: string): string[] => {
    if (!contactsField || !contactsField.trim()) return [];

    return contactsField
      .split(',') // Split by comma
      .map(phone => phone.trim()) // Trim whitespace
      .map(phone => cleanPhoneNumber(phone)) // Clean each number
      .filter(phone => phone.length > 0) // Remove empty
      .filter((phone, index, self) => self.indexOf(phone) === index); // Remove duplicates
  }, [cleanPhoneNumber]);

  /**
   * Handle contact field input with auto-cleanup
   */
  const handleContactInputChange = useCallback((value: string) => {
    // Auto-cleanup on input/paste
    const cleanedNumbers = extractPhoneNumbers(value);
    setFormData(prev => ({ ...prev, Contacs: cleanedNumbers.join(', ') }));

    // Clear error when user starts typing
    if (errors.Contacs) {
      setErrors(prev => ({ ...prev, Contacs: '' }));
    }
  }, [extractPhoneNumbers, errors]);

  // Start improved location tracking when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset location state
      setLocationResult({
        coordinates: null,
        source: null,
        error: null,
        isCenteringOnly: false
      });

      // Get location with fallbacks
      getLocationWithFallbacks().then(result => {
        setLocationResult(result);

        // Update form data if we have coordinates and we're in auto mode
        if (result.coordinates && locationMode === 'auto') {
          setFormData(prev => ({
            ...prev,
            coordinates: result.coordinates
          }));
        }
      });
    } else {
      // Reset when modal closes
      setLocationResult({
        coordinates: null,
        source: null,
        error: null,
        isCenteringOnly: false
      });
      // Reset search states
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);
      // Reset duplicate check states
      setIsCheckingDuplicates(false);
      setDuplicateResult(null);
      setShowDuplicateWarning(false);
      setServerResponse(null);
      setShowServerResponse(false);
      pendingSubmissionDataRef.current = null;
    }
  }, [isOpen, getLocationWithFallbacks, locationMode]);

  // Clear timeout when GPS succeeds
  useEffect(() => {
    if (coordinates && gpsTimeoutId) {
      clearTimeout(gpsTimeoutId);
      setGpsTimeoutId(null);
      setIsGpsTimedOut(false);
    }
  }, [coordinates, gpsTimeoutId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Update form data when GPS coordinates change (only in auto mode)
  useEffect(() => {
    if (coordinates && locationMode === 'auto') {
      setFormData(prev => ({
        ...prev,
        coordinates: coordinates
      }));
    }
  }, [coordinates, locationMode]);

  // Location picker handlers
  const handleOpenLocationPicker = () => {
    setShowLocationPicker(true);
  };

  const handleLocationSelect = (coords: { lat: number; lng: number }) => {
    setManualCoordinates(coords);
    setLocationMode('manual');
    setFormData(prev => ({
      ...prev,
      coordinates: coords
    }));
  };

  const handleSwitchToAutoGPS = () => {
    setLocationMode('auto');
    setManualCoordinates(null);
    if (coordinates) {
      setFormData(prev => ({
        ...prev,
        coordinates: coordinates
      }));
    }
  };

  // Format location status for display with improved fallback system
  const getLocationStatus = () => {
    if (locationMode === 'manual') {
      return {
        status: 'manual',
        message: manualCoordinates ? t('new_customer.location.manual_selected') : t('new_customer.location.manual_prompt'),
        color: manualCoordinates ? 'text-green-600' : 'text-primary',
        canProceed: !!manualCoordinates,
        showRetry: false,
        isCenteringOnly: false
      };
    }

    // Improved auto location mode with fallbacks
    if (locationResult.error) {
      const isNetworkError = locationResult.error.toLowerCase().includes('network') ||
                            locationResult.error.toLowerCase().includes('connection');

      return {
        status: 'error',
        message: isNetworkError
          ? 'Network error - please check connection and retry'
          : locationResult.error,
        color: 'text-red-600',
        canProceed: false,
        showRetry: true,
        isCenteringOnly: false
      };
    }

    if (!locationResult.coordinates) {
      return {
        status: 'searching',
        message: 'Acquiring location...',
        color: 'text-primary',
        canProceed: false,
        showRetry: false,
        isCenteringOnly: false
      };
    }

    // Location acquired - show appropriate message based on source
    const sourceMessages = {
      gps: {
        message: '✅ GPS location acquired - high accuracy',
        color: 'text-green-600',
        isCenteringOnly: false
      },
      telegram: {
        message: '📱 Telegram location acquired - good accuracy',
        color: 'text-green-600',
        isCenteringOnly: false
      },
      ip: {
        message: '🌐 Location acquired from IP - use map below for precise selection',
        color: 'text-orange-600',
        isCenteringOnly: true
      },
      default: {
        message: '🏢 Using default location - please select precise location on map',
        color: 'text-orange-600',
        isCenteringOnly: true
      }
    };

    const sourceInfo = sourceMessages[locationResult.source!];

    return {
      status: 'success',
      message: sourceInfo.message,
      color: sourceInfo.color,
      canProceed: true, // Always allow proceed - user can save with any location, even centering-only ones
      showRetry: sourceInfo.isCenteringOnly, // Show retry for centering-only locations
      isCenteringOnly: sourceInfo.isCenteringOnly
    };
  };

  // Check if submit should be disabled based on location status
  const isSubmitDisabledByLocation = () => {
    const locationStatus = getLocationStatus();

    // Debug logging to understand why submit might be disabled
    console.log('🔍 Submit check:', {
      locationMode,
      hasLocation: !!locationResult.coordinates,
      hasManualCoordinates: !!manualCoordinates,
      canProceed: locationStatus.canProceed,
      status: locationStatus.status,
      source: locationResult.source
    });

    return !locationStatus.canProceed;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-container z-[60]">
      <div className="modal-content-small">
        {/* Header */}
        <div className="bg-card border-b border-border p-4 shadow-sm rounded-t-3xl sm:rounded-t-3xl flex-shrink-0">
          <div className="text-center mb-2">
            <h2 className="text-lg font-semibold text-foreground">{t('new_customer.form.title')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('new_customer.form.subtitle')}</p>
          </div>
          <div className="flex items-center justify-end">
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted/50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={creatingLoading || isCheckingDuplicates}
              title={(creatingLoading || isCheckingDuplicates) ? t('new_customer.form.saving') : ''}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Container */}
        <div className="modal-body">
          <form id="customer-form" onSubmit={handleSubmit} className="modal-content-padding">
          {/* Customer Name */}
          <div className="relative">
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <User className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.name_label')} {t('new_customer.form.name_required')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.NamaPelangganTambak}
                onChange={(e) => handleNameInputChange(e.target.value)}
                className={`w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring ${
                  errors.NamaPelangganTambak ? 'border-destructive' : 'border-border'
                }`}
                placeholder={t('new_customer.form.name_placeholder')}
                disabled={creatingLoading || isCheckingDuplicates}
                autoComplete="off"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                </div>
              )}
              {!isSearching && formData.NamaPelangganTambak && (
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              )}
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div
                ref={searchResultsRef}
                className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {searchResults.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => handleSelectSearchResult(customer)}
                    className="w-full px-3 py-2 text-left hover:bg-muted/50 border-b border-border last:border-b-0 transition-colors"
                    disabled={creatingLoading || isCheckingDuplicates}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground text-sm truncate">
                          {customer.Nama_Pelanggan}
                        </div>
                        {customer.No_pelanggan && (
                          <div className="text-xs text-muted-foreground">
                            No: {customer.No_pelanggan}
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                        Data_Customer
                      </Badge>
                    </div>
                    {customer.Alamat && (
                      <div className="text-xs text-muted-foreground mt-1 truncate">
                        {customer.Alamat}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* No results message */}
            {showSearchResults && searchResults.length === 0 && searchQuery.trim().length >= 2 && !isSearching && (
              <div
                ref={searchResultsRef}
                className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg p-3"
              >
                <div className="text-sm text-muted-foreground text-center">
                  No customers found in Data_Customer
                </div>
              </div>
            )}

            {errors.NamaPelangganTambak && (
              <p className="text-red-500 text-xs mt-1">{errors.NamaPelangganTambak}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.address_label')}
            </label>
            <textarea
              value={formData.Alamat || ''}
              onChange={(e) => handleInputChange('Alamat', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring ${
                errors.Alamat ? 'border-destructive' : 'border-border'
              }`}
              placeholder={t('new_customer.form.address_placeholder')}
              rows={2}
              disabled={creatingLoading}
            />
            {errors.Alamat && (
              <p className="text-red-500 text-xs mt-1">{errors.Alamat}</p>
            )}
          </div>

          {/* Tambak Size */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <Building className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.size_label')}
            </label>
            <input
              type="text"
              value={formData.ukuranTambak || ''}
              onChange={(e) => handleInputChange('ukuranTambak', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring ${
                errors.ukuranTambak ? 'border-destructive' : 'border-border'
              }`}
              placeholder={t('new_customer.form.size_placeholder')}
              disabled={creatingLoading}
            />
            {errors.ukuranTambak && (
              <p className="text-red-500 text-xs mt-1">{errors.ukuranTambak}</p>
            )}
          </div>

          {/* Contact */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <Phone className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.contact_label')}
            </label>
            <input
              type="text"
              value={formData.Contacs || ''}
              onChange={(e) => handleContactInputChange(e.target.value)}
              className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              placeholder="+628123456789, +628987654321"
              disabled={creatingLoading || isCheckingDuplicates}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Format: +628123456789, 08123456789 (comma-separated, 5-20 digits, + prefix optional)
            </p>
          </div>

          {/* Location Selection - Auto GPS or Manual */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.location_label')}
            </label>
            
            {/* Location Mode Selector */}
            <div className="flex space-x-2 mb-3">
              <button
                type="button"
                onClick={handleSwitchToAutoGPS}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border flex items-center justify-center transition-colors ${
                  locationMode === 'auto'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted/50'
                }`}
                disabled={creatingLoading}
              >
                <Navigation className="w-4 h-4 mr-2" />
                {t('new_customer.form.auto_gps')}
              </button>
              <button
                type="button"
                onClick={handleOpenLocationPicker}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border flex items-center justify-center transition-colors ${
                  locationMode === 'manual'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted/50'
                }`}
                disabled={creatingLoading}
              >
                <Map className="w-4 h-4 mr-2" />
                {t('new_customer.form.select_map')}
              </button>
            </div>

            <div className="p-3 border border-border rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  {locationMode === 'auto' ? (
                    locationResult.source === 'gps' ? (
                      <Navigation className="w-4 h-4 mr-2 text-green-600" />
                    ) : locationResult.source === 'ip' ? (
                      <MapPin className="w-4 h-4 mr-2 text-orange-600" />
                    ) : locationResult.source === 'default' ? (
                      <MapPin className="w-4 h-4 mr-2 text-orange-600" />
                    ) : (
                      <Navigation className="w-4 h-4 mr-2 text-primary animate-pulse" />
                    )
                  ) : (
                    <MapPin className="w-4 h-4 mr-2 text-green-600" />
                  )}
                  <span className={`text-sm font-medium ${getLocationStatus().color}`}>
                    {getLocationStatus().message}
                  </span>
                </div>

                {/* Retry button for auto mode */}
                {locationMode === 'auto' && getLocationStatus().showRetry && (
                  <button
                    type="button"
                    onClick={retryLocationAcquisition}
                    className="ml-2 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                    title="Retry location acquisition"
                    disabled={creatingLoading || isCheckingDuplicates}
                  >
                    Retry
                  </button>
                )}
              </div>

              {/* Warning for centering-only locations */}
              {getLocationStatus().isCenteringOnly && (
                <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0" />
                    <p className="text-xs text-orange-800">
                      This location is for map centering only. Please use the map below to select the precise customer location.
                    </p>
                  </div>
                </div>
              )}

              {(locationMode === 'auto' && coordinates) || (locationMode === 'manual' && manualCoordinates) ? (
                <div className="mt-2 p-2 bg-card rounded text-xs text-muted-foreground border border-border">
                  <div className="flex justify-between items-start">
                    <div>
                      {locationMode === 'auto' && coordinates ? (
                        <>
                          <strong>{t('new_customer.form.gps_coordinates')}:</strong> {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                          {accuracy && (
                            <div className="text-muted-foreground mt-1">
                              <strong>{t('new_customer.form.accuracy')}:</strong> ±{accuracy.toFixed(1)}m
                            </div>
                          )}
                        </>
                      ) : manualCoordinates ? (
                        <>
                          <strong>{t('new_customer.form.selected_coordinates')}:</strong> {manualCoordinates.lat.toFixed(6)}, {manualCoordinates.lng.toFixed(6)}
                          <div className="text-muted-foreground mt-1">
                            <strong>{t('new_customer.form.source')}:</strong> {t('new_customer.form.manual_selection')}
                          </div>
                        </>
                      ) : null}
                    </div>
                    {locationMode === 'auto' && lastUpdated && (
                      <div className="text-right text-muted-foreground">
                        <div>{t('new_customer.form.updated')}:</div>
                        <div>{lastUpdated.toLocaleTimeString()}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              
              {locationMode === 'auto' && gpsError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                  <strong>{t('new_customer.form.gps_error')}:</strong> {gpsError}
                  <div className="mt-1 text-xs text-red-500">
                    {t('new_customer.form.gps_error_help')}
                  </div>
                </div>
              )}

              {locationMode === 'auto' && isGpsTimedOut && !coordinates && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                  <strong>{t('new_customer.form.timeout')}:</strong> {t('new_customer.form.timeout_message')}
                  <div className="mt-1 text-xs text-yellow-600">
                    {t('new_customer.form.timeout_help')}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {locationMode === 'auto' 
                ? t('new_customer.form.auto_location_help')
                : t('new_customer.form.manual_location_help')
              }
            </p>
          </div>

          {/* Year Built */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.year_label')}
            </label>
            <input
              type="number"
              value={formData.tahunDibangun || ''}
              onChange={(e) => handleInputChange('tahunDibangun', parseInt(e.target.value) || undefined)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring ${
                errors.tahunDibangun ? 'border-destructive' : 'border-border'
              }`}
              placeholder={t('new_customer.form.year_placeholder')}
              min="1900"
              max={new Date().getFullYear()}
              disabled={creatingLoading}
            />
            {errors.tahunDibangun && (
              <p className="text-red-500 text-xs mt-1">{errors.tahunDibangun}</p>
            )}
          </div>

          {/* Note: PIC field is auto-filled with logged in user name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <UserCheck className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.pic_label')}
            </label>
            <input
              value={currentUserName}
              className="w-full p-3 border border-border rounded-lg text-muted-foreground bg-muted"
              disabled
            />
          </div>

          {/* Approval Notes */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              {t('new_customer.form.description_label')}
            </label>
            <textarea
              value={formData.approval_notes || ''}
              onChange={(e) => handleInputChange('approval_notes', e.target.value)}
              className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              placeholder={t('new_customer.form.notes_placeholder')}
              rows={3}
              disabled={creatingLoading}
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              {t('new_customer.form.status_label')}
            </label>
            <select
              value={formData.statusAktif}
              onChange={(e) => handleInputChange('statusAktif', parseInt(e.target.value))}
              className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              disabled={creatingLoading}
            >
              <option value={1}>{t('status.active')}</option>
              <option value={0}>{t('status.inactive')}</option>
            </select>
          </div>

          </form>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="modal-footer">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={creatingLoading || isCheckingDuplicates}
              title={(creatingLoading || isCheckingDuplicates) ? t('new_customer.form.saving') : ''}
            >
              {t('common.buttons.cancel')}
            </button>
            <button
              type="submit"
              form="customer-form"
              className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              disabled={creatingLoading || isCheckingDuplicates || isSubmitDisabledByLocation()}
              title={isSubmitDisabledByLocation() ? t('new_customer.form.location_required_tooltip') : ''}
            >
              {(creatingLoading || isCheckingDuplicates) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isCheckingDuplicates ? 'Checking...' : t('new_customer.form.saving')}
                </>
              ) : isSubmitDisabledByLocation() ? t('new_customer.form.waiting_location') : t('new_customer.form.save')}
            </button>
          </div>
        </div>
      </div>

      {/* Location Picker Modal */}
      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={manualCoordinates || undefined}
        currentUserLocation={locationResult.coordinates || undefined}
      />

      {/* Loading Overlay for Duplicate Check */}
      {isCheckingDuplicates && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center">
          <div className="bg-card rounded-lg p-6 shadow-xl max-w-sm mx-4 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Checking for Duplicates...</h3>
            <p className="text-sm text-muted-foreground">
              Searching for existing customers with similar names or phone numbers.
            </p>
          </div>
        </div>
      )}

      {/* Duplicate Warning Dialog */}
      <AlertDialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <AlertDialogTitle>Possible Duplicate Found</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              {duplicateResult?.message && (
                <div className="mt-2">
                  <p className="font-medium text-foreground">{duplicateResult.message}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Duplicate Details */}
          <div className="mt-4 space-y-4">
            {duplicateResult?.nameMatches && duplicateResult.nameMatches.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Matching Names ({duplicateResult.nameMatches.length})
                </h4>
                <div className="space-y-2">
                  {duplicateResult.nameMatches.map((customer) => (
                    <div key={`${customer.source_table}-${customer.id}`} className="bg-background rounded p-3 border">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{customer.Nama_Pelanggan}</p>
                            <Badge
                              variant={customer.source_table === 'Data_Customer' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {customer.source_table}
                            </Badge>
                          </div>
                          {customer.No_pelanggan && (
                            <p className="text-sm text-muted-foreground">No: {customer.No_pelanggan}</p>
                          )}
                          {customer.Alamat && (
                            <p className="text-sm text-muted-foreground mt-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {customer.Alamat}
                            </p>
                          )}
                          {/* Show map pin if coordinates exist */}
                          {(customer.latitude && customer.longitude) && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded text-xs flex items-center gap-2">
                              <Map className="w-3 h-3 text-blue-600" />
                              <span className="text-blue-800 dark:text-blue-200">
                                Location: {customer.latitude.toFixed(6)}, {customer.longitude.toFixed(6)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {duplicateResult?.phoneMatches && duplicateResult.phoneMatches.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Matching Phone Numbers ({duplicateResult.phoneMatches.length})
                </h4>
                <div className="space-y-2">
                  {duplicateResult.phoneMatches.map((phone) => (
                    <div key={phone.id} className="bg-background rounded p-3 border">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <p className="font-medium">{phone.Phone_Parsed || phone.Phone_Number}</p>
                        {phone.No_pelanggan && (
                          <span className="text-sm text-muted-foreground ml-auto">
                            Customer: {phone.No_pelanggan}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleDuplicateWarningResponse(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDuplicateWarningResponse(true)}
              className="bg-primary"
            >
              Keep Adding
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Server Response Dialog */}
      <AlertDialog open={showServerResponse} onOpenChange={handleServerResponseClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              {serverResponse?.success ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <AlertDialogTitle>
                {serverResponse?.success ? 'Success' : 'Error'}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              {serverResponse?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {serverResponse?.success && serverResponse.customer && (
            <div className="mt-4 bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Customer farm has been created successfully. You can now proceed with other operations.
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogAction onClick={handleServerResponseClose}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};